import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  ZoomControl,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { loadEpreuves } from "../requests";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";

const iconTriathlon = new L.Icon({
  iconUrl: require("../medias/pin-triathlon-map.png"),
  iconRetinaUrl: require("../medias/pin-triathlon-map.png"),
  iconAnchor: null,
  popupAnchor: [0, -18],
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
  tooltipAnchor: null,
  iconSize: new L.Point(30, 40),
  className: "triathlonMarker",
});

export function coord(value) {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    const match = value.match(/\(([^,]+),([^)]+)\)/);

    if (!match) {
      return null;
    }

    const lat = Number(match[1].trim());
    const lng = Number(match[2].trim());

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return [lat, lng];
  } catch (err) {
    return null;
  }
}

function BoxZoomButton({ position = "bottomleft" }) {
  const map = useMap();

  useEffect(() => {
    if (map.boxZoom) {
      map.boxZoom.enable();
    }
  }, [map]);

  const positionClass =
    {
      topleft: "leaflet-top leaflet-left",
      topright: "leaflet-top leaflet-right",
      bottomleft: "leaflet-bottom leaflet-left",
      bottomright: "leaflet-bottom leaflet-right",
    }[position] || "leaflet-bottom leaflet-left";

  return (
    <div className={positionClass}>
      <div className="leaflet-control leaflet-bar">
        <button
          type="button"
          title="Maintenez Shift et glissez pour zoomer sur une zone"
          aria-label="Zoom rectangle"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            fontSize: "16px",
            border: "none",
            background: "white",
            cursor: "pointer",
          }}
        >
          ⬚
        </button>
      </div>
    </div>
  );
}

function MapLocation({
  coord: coordProp,
  popup,
  yearSelected,
  region,
  zoomSize,
}) {
  const [epreuves, setEpreuves] = useState([]);
  const [mapInstance, setMapInstance] = useState(null);
  const markers = coordProp || [47, 2.3488];
  const year = yearSelected || 2023;

  // Leaflet calcule sa taille au moment du montage. Dans un conteneur
  // qui n'a pas encore sa taille finale à cet instant (carte dans une
  // colonne étroite avec padding, layout flex pas encore stabilisé...),
  // la carte se fige à une mauvaise taille et déborde visuellement.
  // On force un recalcul juste après le montage, et à chaque
  // redimensionnement de la fenêtre.
  useEffect(() => {
    if (!mapInstance) return;

    const invalidate = () => mapInstance.invalidateSize();

    // Le conteneur parent (ex: .side-card--map) peut ne pas être encore
    // stabilisé au tout premier rendu : un léger délai suffit à laisser
    // le layout se poser avant de mesurer.
    const timeoutId = setTimeout(invalidate, 100);

    window.addEventListener("resize", invalidate);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", invalidate);
    };
  }, [mapInstance]);

  useEffect(() => {
    let cancelled = false;

    async function fetchEpreuves() {
      try {
        const result = await loadEpreuves({
          region: "",
          name: "",
          month: 0,
          year:
            year.value || Number(window.localStorage.getItem("year")) || 2023,
          type: ["XS", "S", "M", "L", "XL", "XXL"],
          relay: "",
          Elevation_bike: 0,
          bibs: 25000,
          Swimenv: ["Piscine", "Lac", "Mer", "Rivière", "Plan d'eau", "0"],
          crosstri: ["1", "0"],
        });

        // Filtrage des coordonnées invalides
        const validEpreuves = result.filter((epreuve) => {
          const position = coord(epreuve.map_coord);

          if (!position) {
            console.warn(
              `Épreuve ignorée (${epreuve.id}) : coordonnées invalides`,
              {
                nom: epreuve.name_event,
                map_coord: epreuve.map_coord,
              }
            );
            return false;
          }

          return true;
        });

        console.info(
          `${validEpreuves.length}/${result.length} épreuves chargées.`
        );

        if (!cancelled) {
          setEpreuves(validEpreuves);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des épreuves :", error);
      }
    }

    fetchEpreuves();

    return () => {
      cancelled = true;
    };
  }, [year]);

  return (
    <div className="map p-0" style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <MapContainer
        center={markers}
        zoom={zoomSize || 12}
        maxZoom={18}
        zoomControl={false}
        whenCreated={setMapInstance}
        style={{
          position: "relative",
          top: 0,
          left: 0,
          height: "25rem",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <TileLayer
          attribution=""
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {epreuves.map((epreuve) => (
          <Marker
            key={epreuve.id}
            position={coord(epreuve.map_coord)}
            icon={iconTriathlon}
          >
            <Popup className="p-0">
              <Link
                to={`/epreuve/${epreuve.id}`}
                className="text-warning"
              >
                <span className="font-weight-bold">
                  {epreuve.name_event}
                </span>
              </Link>

              <br />

              <span className="text-muted">
                {epreuve.date.slice(0, 10)} :{" "}
                {epreuve.races && epreuve.races.length > 0
                  ? epreuve.races.map((item) => item.type_triathlon + " ")
                  : "Format non renseigné"}
              </span>
            </Popup>
          </Marker>
        ))}

        <ZoomControl position="topright" />
        <BoxZoomButton position="bottomleft" />
      </MapContainer>
    </div>
  );
}

export default MapLocation;
